import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { analyticsService } from '../api/index';
import { Award, Clock, TrendingUp, ChevronRight, Star, Zap, Target } from 'lucide-react';

// ─── Tiny SVG sparkline ───────────────────────────────────────────────────────
function Sparkline({ data, color = '#2F7C7B', height = 48, metric = 'minutes' }) {
  if (!data || data.length === 0) return <div style={{ height }} />;
  const vals = data.map((d) => d[metric] || d.minutes || d.hours || d.items || 0);
  const max = Math.max(...vals, 1);
  const w = 200;
  const h = height;
  const pts = vals
    .map((v, i) => `${(i / (vals.length - 1)) * w},${h - (v / max) * h}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height }} preserveAspectRatio="none">
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points={`0,${h} ${pts} ${w},${h}`}
        fill={`${color}22`}
      />
    </svg>
  );
}

// ─── Radial progress ring ────────────────────────────────────────────────────
function ProgressRing({ pct, size = 72, stroke = 7, color = '#2F7C7B' }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-border)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize="13" fontWeight="700" fill="var(--color-text-primary)">
        {pct}%
      </text>
    </svg>
  );
}

// ─── Stat card ───────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div style={{
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 16,
      padding: '24px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -20, right: -20,
        width: 80, height: 80, borderRadius: '50%',
        background: `${color}18`,
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ background: `${color}22`, borderRadius: 10, padding: 8, display: 'flex' }}>
          <Icon size={20} style={{ color }} />
        </div>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--color-text-primary)', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{sub}</div>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await analyticsService.getMyAnalytics();
      setData(res.data);
    } catch (e) {
      setError('Could not load analytics data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activity = data?.learning_activity;
  const trendHasMinutes = (data?.learning_trend || []).some((d) => (d.minutes || 0) > 0);
  const trendMetric = trendHasMinutes ? 'minutes' : 'items';

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
        {/* ── Header ── */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, color: 'var(--color-text-primary)' }}>
            My Learning Analytics
          </h1>
          <p style={{ margin: '6px 0 0', color: 'var(--color-text-secondary)', fontSize: 14 }}>
            Track your progress, time invested, and achievements — all in one place.
          </p>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 80, color: 'var(--color-text-secondary)' }}>
            Loading analytics…
          </div>
        )}
        {error && (
          <div style={{ textAlign: 'center', padding: 80, color: 'var(--color-error, #ef4444)' }}>
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            {/* ── Stat Strip (Coursera-inspired) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              <StatCard
                icon={Clock}
                label="Minutes Learned"
                value={`${activity?.minutes_last_4_weeks ?? 0}m`}
                sub="Last 4 weeks"
                color="#2F7C7B"
              />
              <StatCard
                icon={Target}
                label="Items Completed"
                value={activity?.items_completed_last_4_weeks ?? 0}
                sub="Lessons finished · last 4 weeks"
                color="#10b981"
              />
              <StatCard
                icon={TrendingUp}
                label="This Week"
                value={`${activity?.items_completed_this_week ?? 0} items`}
                sub={`${activity?.minutes_this_week ?? 0} minutes learned`}
                color="#f59e0b"
              />
              <StatCard
                icon={Award}
                label="Certificates Earned"
                value={data.certificates.length}
                color="#2FA7A1"
              />
            </div>

            {/* ── Last 4 weeks + streak ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 24 }}>
              <div style={{
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 16,
                padding: 24,
              }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: 'var(--color-text-primary)' }}>
                  Last 4 weeks
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, textAlign: 'center' }}>
                  <div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--color-text-primary)' }}>
                      {activity?.items_completed_last_4_weeks ?? 0}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Items completed</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--color-text-primary)' }}>
                      {activity?.minutes_last_4_weeks ?? 0}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Minutes learned</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--color-text-primary)' }}>
                      {activity?.items_completed ?? 0}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>All-time completions</div>
                  </div>
                </div>
                {activity?.data_source === 'lesson_completions' && (
                  <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    Time is estimated from completed lessons until live lesson tracking is enabled.
                  </p>
                )}
              </div>

              <div style={{
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 16,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Zap size={18} style={{ color: '#F2B542' }} />
                  <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    {(activity?.streak_days ?? 0) > 0 ? `${activity.streak_days} day streak` : 'Start your streak'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                  {(data.weekly_activity || []).map((day) => (
                    <div key={day.date} style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{
                        width: 28, height: 28, margin: '0 auto 4px',
                        borderRadius: 8,
                        border: '1px solid var(--color-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: day.has_activity ? 'rgba(167,139,250,0.2)' : 'transparent',
                        color: day.has_activity ? '#F2B542' : 'var(--color-text-muted)',
                        fontSize: 11, fontWeight: 700,
                      }}>
                        {day.has_activity ? '✓' : ''}
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{day.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Learning Trend Chart ── */}
            <div style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 16,
              padding: 24,
              marginBottom: 24,
            }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: 'var(--color-text-primary)' }}>
                Daily Learning Activity (last 30 days)
              </div>
              {data.learning_trend.length > 0 ? (
                <>
                  <Sparkline
                    data={data.learning_trend}
                    color="#2F7C7B"
                    height={80}
                    metric={trendMetric}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                      {data.learning_trend[0]?.date}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                      {trendHasMinutes ? 'Minutes per day' : 'Items completed per day'}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                      {data.learning_trend[data.learning_trend.length - 1]?.date}
                    </span>
                  </div>
                </>
              ) : (
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
                  No activity data yet. Complete some lessons to see your trend!
                </p>
              )}
            </div>

            {/* ── Active Courses ── */}
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16, color: 'var(--color-text-primary)' }}>
                Active Courses
              </h2>
              {data.active_enrollments.length === 0 && (
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>No active enrolments yet.</p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {data.active_enrollments.map((enr) => (
                  <div key={enr.enrollment_id} style={{
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 14,
                    padding: '18px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                  }}>
                    <ProgressRing pct={enr.progress_percent} color="#2F7C7B" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {enr.course_title}
                      </div>
                      <div style={{ position: 'relative', height: 6, borderRadius: 999, background: 'var(--color-border)' }}>
                        <div style={{
                          position: 'absolute', left: 0, top: 0, height: '100%',
                          width: `${enr.progress_percent}%`,
                          borderRadius: 999,
                          background: 'linear-gradient(90deg, #2F7C7B, #2FA7A1)',
                          transition: 'width 0.8s ease',
                        }} />
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 6 }}>
                        {enr.completed_lessons} / {enr.total_lessons} lessons · Status: <strong>{enr.status}</strong>
                      </div>
                    </div>
                    <ChevronRight size={18} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            </div>

            {/* ── Bottom Row: Grades + Certificates ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Grades */}
              <div style={{
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 16,
                padding: 22,
              }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: 'var(--color-text-primary)' }}>
                  Recent Grades
                </h2>
                {data.grades.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>No graded assessments yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {data.grades.slice(0, 6).map((g, i) => {
                      const pct = g.max_score ? Math.round((g.score / g.max_score) * 100) : null;
                      const color = pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            minWidth: 42, textAlign: 'center',
                            fontWeight: 800, fontSize: 13,
                            color,
                          }}>
                            {pct != null ? `${pct}%` : '—'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {g.assessment_title}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{g.course_title}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Certificates */}
              <div style={{
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 16,
                padding: 22,
              }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: 'var(--color-text-primary)' }}>
                  Certificates Earned
                </h2>
                {data.certificates.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
                    Complete a course to earn your first certificate!
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {data.certificates.map((c) => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ background: '#f59e0b22', borderRadius: 8, padding: 6, display: 'flex' }}>
                          <Star size={16} style={{ color: '#f59e0b' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.course_title}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                            {c.issued_at ? new Date(c.issued_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
